import React, { useContext, useRef } from 'react'
import * as R from 'ramda'
import { graphql } from 'gatsby'
import loadable from '@loadable/component'
import ProductItem from '../components/product/ProductItem'
import { LayoutContext } from '../contexts'
import SEO from '../components/SEO'
import useShopifyProduct from '../hooks/useShopifyProduct'
import { useSchemaForLD } from '../hooks/useSchemaForLD'
import { decode } from 'shopify-gid'
import { getLocale } from '../components/CurrencyConverterSwitcher'
import { useFetchReviews } from '../hooks/useFetchReviews'
import { useGlobal } from '../hooks/useGlobal'
import { getShopifyData } from '../utils'

const ProductTechnology = loadable(() =>
  import('../components/product/ProductTechnology'),
)
const ProductInspection = loadable(() =>
  import('../components/product/ProductInspection'),
)
const FamilyReviews = loadable(() =>
  import('../components/product/FamilyReviews'),
)
const FeaturedCollection = loadable(() =>
  import('../components/shared/FeaturedCollection'),
)

const Product = ({
  location,
  data: {
    contentfulProduct: {
      name,
      title,
      images,
      family,
      gender,
      shortenedDescription,
      productBullets,
      instagramUsername,
      color,
      slug,
      shopifyProduct,
      shopifyProduct2,
      shopifyData,
      shopifyDataUs,
      instagramImage,
      featuredImage,
      inspectionImage,
      productBundle,
      relatedProducts,
      seoTitle,
      seoDescription,
      sizeSelect,
      productType,
      labelTopRight,
      labelTopRightImage,
      labelTopRightText,
      labelBottomRight,
      labelBottomRightImage,
      labelBottomRightText,
      label,
      logo,
      outOfStock,
      notComingBack,
      productAccordions,
      thumbnailImage,
      canonicalLink,
      amazonUrl,
      amazonPriceValue,
      sizeInfo,
      removeIndividualTieredDiscountMessage,
      multipackProductInfo,
      preorder,
      preorderMessage,
    },
  },
}) => {
  const { product } = useShopifyProduct(shopifyProduct.handle)
  const { node_locale, currency } = useContext(LayoutContext)

  const giftProducts =
    product?.handle === 'gift-wrapping' || product?.handle === 'stox-gift-card'

  const {
    product: { productPageRelatedProducts },
  } = useGlobal(['product'], node_locale)

  const reviewEl = useRef()
  const inspectEl = useRef()
  const onViewReviews = () =>
    window.scrollTo({
      top: reviewEl?.current?.offsetTop ? reviewEl.current.offsetTop + 50 : 0,
      behavior: 'smooth',
    })
  const onViewDescription = () =>
    window.scrollTo({
      top: inspectEl?.current?.offsetTop ? reviewEl.current.offsetTop + 50 : 0,
      behavior: 'smooth',
    })

  const onToTop = () =>
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

  //------------- SEO schema logic
  const idForSeo =
    getShopifyData({ shopifyData, shopifyDataUs }).shopifyId &&
    decode(getShopifyData({ shopifyData, shopifyDataUs }).shopifyId).id
  const skuForSeo = R.join(',', family.legacySkus)
  const offerUrl = `https://www.stoxenergy.com/${node_locale}/${slug}/`
  const priceCurrency = currency?.code
  const { reviews, count, score } = useFetchReviews({ family })

  const getCurrentPrice = () => {
    const normalPrice = shopifyData?.variants?.[0]?.presentmentPrices?.find(
      (obj) => obj?.price?.currencyCode === priceCurrency,
    )?.price?.amount

    const discountedPrice =
      product?.variants?.edges[0]?.node?.presentmentPrices?.edges?.find(
        ({ node }) => {
          return node.price.currencyCode === priceCurrency
        },
      )?.node.price.amount

    const currentPrice =
      node_locale !== 'en-US' && discountedPrice ? discountedPrice : normalPrice
    return currentPrice
  }

  return (
    <>
      <SEO
        isProduct
        location={location}
        title={seoTitle}
        description={seoDescription}
        image={images?.[0] && images?.[0]?.file?.url}
        canonicalLink={canonicalLink}
        pageCategory={'Product'}
        referrer={location.state?.referrer}
        schemas={[
          useSchemaForLD('breadcrumb', {
            item: location.pathname,
            name: title,
            position: 3,
          }),
          useSchemaForLD('product', {
            id: idForSeo,
            name: seoTitle,
            description: seoDescription,
            image: images?.map(({ file: { url } }) => 'https:' + url),
            sku: skuForSeo,
            offerUrl: offerUrl,
            priceCurrency: priceCurrency,
            price: getCurrentPrice(),
            color: family.product,
            material: family.material,
            //----NOT SURE IF THIS IS NEEDED BUT REPORT SAID TO INCLUE - RICH TEST THROWS ERROR
            // isRelatedTo: relatedProducts,
            //--------------------------------
            aggregateRating: {
              ratingValue: score,
              reviewCount: count,
            },
            review:
              reviews && node_locale === 'de'
                ? {
                    reviewRating: {
                      ratingValue: reviews[0]?.mark,
                    },
                    author: {
                      name:
                        reviews[0]?.reviewer?.email
                          .split('@')[0]
                          .charAt(0)
                          .toUpperCase() +
                        reviews[0]?.reviewer?.email.split('@')[0].slice(1),
                    },
                    reviewBody: reviews[0]?.comment,
                  }
                : {
                    reviewRating: {
                      ratingValue: reviews[0]?.stars,
                    },
                    author: {
                      name: reviews[0]?.consumer?.displayName,
                    },
                    reviewBody: reviews[0]?.content,
                  },
          }),
        ]}
      />

      <ProductItem
        product={product}
        location={location}
        name={name}
        title={title}
        images={images}
        family={family}
        gender={gender}
        color={color}
        productSlug={slug}
        shopifyProduct={shopifyProduct}
        shopifyProduct2={shopifyProduct2}
        multipackProductInfo={multipackProductInfo}
        shortenedDescription={shortenedDescription}
        productBullets={productBullets}
        sizeSelect={sizeSelect}
        onViewReviews={onViewReviews}
        onToTop={onToTop}
        onViewDescription={onViewDescription}
        productType={productType}
        label={label}
        outOfStockMessages={outOfStock}
        relatedProducts={relatedProducts}
        productBundle={productBundle}
        productAccordions={productAccordions}
        thumbnailImage={thumbnailImage}
        notComingBack={notComingBack}
        position={1}
        amazonUrl={amazonUrl}
        amazonPriceValue={amazonPriceValue}
        logo={logo}
        description={seoDescription}
        sizeInfo={sizeInfo}
        labelTopRight={labelTopRight}
        labelTopRightImage={labelTopRightImage}
        labelTopRightText={labelTopRightText}
        labelBottomRight={labelBottomRight}
        labelBottomRightImage={labelBottomRightImage}
        labelBottomRightText={labelBottomRightText}
        removeIndividualTieredDiscountMessage={
          removeIndividualTieredDiscountMessage
        }
        preorder={preorder}
        preorderMessage={preorderMessage}
      />
      {relatedProducts && (
        <FeaturedCollection
          heading={productPageRelatedProducts}
          featuredCollection={relatedProducts}
          location={location}
          viewType='Product Page'
          position={2}
        />
      )}
      <>
        {family && !giftProducts && (
          <>
            <ProductTechnology
              instagramImage={instagramImage}
              instagramUsername={instagramUsername}
              family={family}
              featuredImage={featuredImage}
              position={3}
            />
            <ProductInspection
              family={family}
              inspectionImage={inspectionImage}
              ref={inspectEl}
              title={title}
              position={4}
            />
            <FamilyReviews
              family={family}
              title={title}
              name={name}
              ref={reviewEl}
              images={images}
              location={location}
              description={seoDescription}
              slug={slug}
              relatedProducts={relatedProducts}
              shopifyProduct={shopifyProduct}
              position={5}
            />
          </>
        )}
      </>
    </>
  )
}

export const ProductQuery = graphql`
  query Product($id: String!) {
    contentfulProduct(id: { eq: $id }) {
      seoTitle
      seoDescription
      sizeInfo
      name
      title
      slug
      removeIndividualTieredDiscountMessage
      preorder
      preorderMessage
      labelTopRight
      labelTopRightImage {
        fluid(sizes: "(max-width: 1023px) 92vw, 41vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      labelTopRightText
      labelBottomRight
      labelBottomRightImage {
        fluid(sizes: "(max-width: 1023px) 92vw, 41vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      labelBottomRightText
      inspectionImage {
        title
        fluid(sizes: "(max-width: 1023px) 53vw, 22vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      sizeSelect
      shopifyProduct {
        handle
        variants {
          edges {
            node {
              price
            }
          }
        }
      }
      shopifyProduct2 {
        handle
        variants {
          edges {
            node {
              price
            }
          }
        }
      }
      shopifyData {
        shopifyId
        variants {
          presentmentPrices {
            price {
              currencyCode
              amount
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
        }
      }
      shopifyDataUs {
        shopifyId
        variants {
          presentmentPrices {
            price {
              currencyCode
              amount
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
        }
      }
      family {
        name
        legacySkus
        primaryColor
        secondaryColor
        product {
          availableInCountry
          title
          name
          slug
          color {
            title
            color
          }
        }
        collection {
          slug
          title
        }
      }
      color {
        title
        color
      }
      thumbnailImage {
        file {
          url
        }
      }
      images {
        title
        file {
          url
        }
        fluid(sizes: "(max-width: 1023px) 75vw, 45vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      instagramUsername
      instagramImage {
        description
        title
        fluid(sizes: "(max-width: 1023px) 100vw, 50vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      featuredImage {
        description
        title
        fluid(sizes: "(max-width: 1023px) 100vw, 50vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      family {
        name
        evidence {
          raw
        }
        compression
        material
        image {
          title
          fluid(sizes: "(max-width: 1023px) 100vw, 50vw") {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
        videoTitle
        video {
          name
          videoMp4 {
            file {
              url
            }
          }
          videoWebm {
            file {
              url
            }
          }
          poster {
            title
            description
            fluid(sizes: "(max-width: 1023px) 100vw, 50vw") {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
        }
        showVideoTitleAndDescription
        videoTitle
        videoDescription
        primaryColor
        legacySkus
        inspectionItems {
          title
          name
          content {
            raw
          }
        }
      }
      productType {
        name
        menSizeText
        menSizeTableHeader
        menTabel {
          raw
        }
        womenSizeText
        womenSizeTableHeader
        womenTabel {
          raw
        }
        unisexText
        unisexSizeTableHeader
        unisexTabel {
          raw
        }
        sizeTableMessage
        menSizesV2 {
          sizeName
          ankleRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          calfRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          lowerLegRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          shoeRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            minValueUs
            maxValueUs
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
        }
        womenSizesV2 {
          sizeName
          ankleRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          calfRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          lowerLegRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          shoeRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            minValueUs
            maxValueUs
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
        }
        unisexSizesV2 {
          sizeName
          ankleRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          calfRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          lowerLegRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
          shoeRanges {
            sizeName
            measurementName
            minValue
            maxValue
            minValueUk
            maxValueUk
            minValueUs
            maxValueUs
            subMeasurements {
              sizeName
              measurementName
              minValue
              maxValue
              minValueUk
              maxValueUk
              minValueUs
              maxValueUs
            }
          }
        }
      }
      gender {
        title
        name
      }
      shortenedDescription {
        shortenedDescription
      }
      multipackProductInfo {
        title
        bullets
      }
      productBundle {
        title
        description
        label
        addToCartText
        originalPrice
        discountedPrice
        productImages {
          fluid(sizes: "(max-width: 1023px) 75vw, 45vw") {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
        products {
          shopifyProduct {
            handle
            variants {
              edges {
                node {
                  price
                }
              }
            }
          }
          productType {
            name
          }
          gender {
            name
          }
        }
      }
      relatedProducts {
        availableInCountry
        id
        title
        label
        logo {
          title
          fluid(sizes: "(max-width: 1023px) 92vw, 41vw") {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
        family {
          legacySkus
          name
        }
        slug
        thumbnailSubheading
        thumbnailImage {
          title
          fluid(sizes: "(max-width: 1023px) 60vw, 25vw") {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
        shopifyProduct {
          handle
          variants {
            edges {
              node {
                price
              }
            }
          }
        }
      }
      productBullets {
        title
        bullets
      }
      outOfStock {
        size
        message
      }
      notComingBack
      productAccordions {
        id
        name
        title
        content {
          raw
          references {
            ... on ContentfulAccordionLink {
              contentful_id
              slug
            }
            ... on ContentfulTextPage {
              slug
              contentful_id
            }
          }
        }
        svg {
          file {
            url
          }
          svg {
            content
          }
        }
      }
      canonicalLink {
        slug
      }
      amazonUrl
      amazonPriceValue
      label
      logo {
        title
        fluid(sizes: "(max-width: 1023px) 92vw, 41vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
    }
  }
`

export default Product
